/**
 * useChatAttachmentUrl — resolve a chat attachment ref to a fresh download URL.
 *
 * Dual-read: channel-scoped `catt_` ids use the channel attachment endpoint
 * (membership-gated); legacy `vdoc_` ids fall back to the Vault download
 * (read-shim) so attachments on old messages keep rendering. Returns the URL
 * string, or null while loading / on error.
 *
 * `disposition` defaults to `inline` so the URL renders in-page (thumbnails and
 * the viewer modal) instead of forcing a download; pass `attachment` for an
 * explicit save-to-device URL.
 */

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

export function useChatAttachmentUrl(
  channelId: string | null,
  fileId: string | null | undefined,
  disposition: 'inline' | 'attachment' = 'inline',
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    const resolve: Promise<string> = fileId.startsWith('catt_')
      ? channelId
        ? api.getChatAttachmentDownloadUrl(channelId, fileId, disposition).then((r) => r.url)
        : Promise.reject(new Error('No channel for chat attachment'))
      : api.getVaultDocumentDownloadUrl(fileId).then((r) => r.url);

    resolve
      .then((u) => {
        if (!cancelled) setUrl(u);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [channelId, fileId, disposition]);

  return url;
}
